import { Suspense } from 'react'

import { createBrowserRouter, RouterProvider } from 'react-router-dom'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { PrimeReactProvider } from 'primereact/api'

import AuthLayout from './Components/Layouts/AuthLayout'
import BackLayout from './Components/Layouts/BackLayout'

import NotFoundPage from './Components/Errors/Pages/NotFoundPage'
import RouterError  from './Components/Errors/RouterError'

import LayoutLoader from './Components/Loaders/LayoutLoader'

import RootSpinner from './Components/Spinners/RootSpinner'

import AppVersion from './Components/Configs/AppVersion'

import ROUTES from './routes'
import FrontLayout from './Components/Layouts/FrontLayout.jsx'
import ArticleLoader from './Components/Loaders/ArticleLoader.jsx'
import CategoryLoader from './Components/Loaders/CategoryLoader.jsx'
import PropertiesLoader from './Components/Loaders/PropertiesLoader.jsx'
import PropertyDataLoader from './Components/Loaders/PropertyDataLoader.jsx'
import FrontLoader from './Components/Loaders/FrontLoader.jsx'
import BlogLoader from './Components/Loaders/BlogLoader.jsx'

const prime_params = { ripple: true, unstyled: true };

const routes =
[
	{
		path: '*',
		errorElement: RouterError,
		element: <NotFoundPage error="Cette page n'existe pas." center />
	},

	{
		path: '/',
		errorElement: RouterError,
		loader: LayoutLoader,
		element: <FrontLayout />,

		children:
		[
			{
				path: '',
				lazy: ROUTES.HomePage,
				loader: FrontLoader
			},
			{
				path: 'qui-sommes-nous',
				lazy: ROUTES.OsabaPage
			},
			{
				path: 'rejoindre-osaba',
				lazy: ROUTES.RejoindreEquipePage
			},
			{
				path: 'vendre-estimer',
				lazy: ROUTES.VendreEstimerPage
			},
			{
				path: 'beosaba',
				lazy: ROUTES.BeOsabaPage
			},
			{
				path: 'mentions-legales',
				lazy: ROUTES.MentionsPage
			},
			{
				path: 'acheter',
				lazy: ROUTES.BuyPage,
				loader: PropertiesLoader
			},
			{
				path: 'contact',
				lazy: ROUTES.ContactPage
			},
			{
				path: 'honoraires',
				lazy: ROUTES.HonorairesPage
			}
		]
	},

	{
		path: '/biens',
		errorElement: RouterError,
		loader: LayoutLoader,
		element: <FrontLayout />,

		children:
		[
			{
				path: '',
				lazy: ROUTES.PropertiesPage,
				loader: PropertiesLoader
			},
			{
				path: ':bien_reference',
				lazy: ROUTES.PropertyPage,
				loader: PropertyDataLoader
			},

		]
	},

	{
		path: '/createurs-de-patrimoine',
		errorElement: RouterError,
		loader: LayoutLoader,
		element: <FrontLayout />,

		children:
		[
			{
				path: '',
				lazy: ROUTES.CreateursPatrimoinePage
			},
			{
				path: 'rehabilitation-renovation',
				lazy: ROUTES.RehabilitationRenovationPage
			},
			{
				path: 'division-amenagement',
				lazy: ROUTES.DivisionAmenagementPage
			},
			{
				path: 'conseil-montage',
				lazy: ROUTES.ConseilMontagePage
			}
		]
	},

	{
		path: '/blog',
		errorElement: RouterError,
		loader: LayoutLoader,
		element: <FrontLayout />,

		children:
		[
			{
				path: '',
				lazy: ROUTES.BlogPage,
				loader: BlogLoader
			},
			{
				path: ':slug',
				lazy: ROUTES.ArticlePage,
				loader: ArticleLoader
			},
			{
				path: 'categories/:categorie',
				lazy: ROUTES.CategoriePage,
				loader: CategoryLoader
			}
		]
	},

	{
		path: '/',
		errorElement: RouterError,
		loader: LayoutLoader,
		element: <AuthLayout />,

		children:
		[
			{
				path: 'login',
				lazy: ROUTES.LoginPage
			},
			{
				path: 'mot-de-passe-oublie',
				lazy: ROUTES.RequestPasswordPage
			},
			{
				path: 'mot-de-passe-oublie/nouveau-mot-de-passe/:token',
				lazy: ROUTES.NewPasswordPage
			}
		]
	},

	{
		path: 'mon-compte',
		errorElement: RouterError,
		loader: LayoutLoader,
		element: <BackLayout />,

		children:
		[
			{
				path: '',
				lazy: ROUTES.MyAccountPage
			}
		]
	},

	{
		path: '/admin',
		errorElement: RouterError,
		loader: LayoutLoader,
		element: <BackLayout />,

		children:
		[
			{
				path: 'accueil',
				lazy: ROUTES.AdminHomePage
			},
			{
				path: 'liste-des-utilisateurs',
				lazy: ROUTES.UsersListPage
			},
			{
				path: 'liste-des-utilisateurs/nouvel-utilisateur',
				lazy: ROUTES.NewUserPage
			},
			{
				path: 'liste-des-utilisateurs/:user_id',
				lazy: ROUTES.UserPage
			},
			{
				path: 'liste-des-actualites',
				lazy: ROUTES.NewsListPage
			},
			{
				path: 'liste-des-actualites/nouveau',
				lazy: ROUTES.NewNewsPage
			},
			{
				path: 'liste-des-actualites/:id',
				lazy: ROUTES.UpdateNewsPage
			}
		]
	}
];

const FUTURE_FLAGS =
{
	v7_startTransition: true,
	v7_relativeSplatPath: true,
	v7_fetcherPersist: true,
	v7_skipActionErrorRevalidation: true
};

const router = createBrowserRouter(routes,
{
	future: FUTURE_FLAGS
});

const tanstack_query_client = new QueryClient();

window.__TANSTACK_QUERY_CLIENT__ = tanstack_query_client;

export default function App ()
{
	return (
		<QueryClientProvider client={ tanstack_query_client }>
			<PrimeReactProvider value={ prime_params }>

				<Suspense fallback={ <RootSpinner active /> }>
					<RouterProvider router={ router } />
				</Suspense>

				<AppVersion />

			</PrimeReactProvider>
		</QueryClientProvider>
	);
}